import { useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonSecondaire, MessageErreur, MarqueObligatoire } from '../ui/Primitives';

/** Motifs les plus fréquents d'un exemplaire signé rendu inexploitable. */
const MOTIFS_COURANTS = [
  'Exemplaire non signé',
  'Signature manquante d’une des parties',
  'Document illisible ou trop sombre',
  'Il manque une page',
  'Ce n’est pas la bonne version du document',
] as const;

/**
 * Refus motivé d'un exemplaire signé déposé en retour.
 *
 * L'API exige le motif — `schemaControleSigne` refuse un `REFUSE` sans
 * `motifRefus`. Le motif reste interne : il n'est pas transmis au candidat,
 * mais il dit à qui redéposera ce qui manque, faute de quoi le même fichier
 * revient.
 */
export function RefusDocumentSigne({
  ouverte,
  libelleDocument,
  enCours,
  erreur,
  onRefuser,
  onFermer,
}: {
  ouverte: boolean;
  libelleDocument: string;
  enCours: boolean;
  erreur: string | null;
  onRefuser: (motif: string) => void;
  onFermer: () => void;
}): JSX.Element {
  const [motif, setMotif] = useState('');

  useEffect(() => {
    if (ouverte) setMotif('');
  }, [ouverte]);

  const envoyer = (evenement: React.FormEvent): void => {
    evenement.preventDefault();
    const propre = motif.trim();
    if (!propre) return;
    onRefuser(propre);
  };

  return (
    <Modale
      titre="Refuser cet exemplaire signé"
      sousTitre={`${libelleDocument} · le motif reste interne au dossier`}
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-xl"
    >
      <form onSubmit={envoyer} className="space-y-4 px-4 sm:px-6 py-5">
        {erreur && <MessageErreur message={erreur} />}

        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-codialis-gray">
            Motifs courants
          </span>
          <div className="flex flex-wrap gap-2">
            {MOTIFS_COURANTS.map((courant) => (
              <button
                key={courant}
                type="button"
                onClick={() => setMotif(courant)}
                aria-pressed={motif === courant}
                className={`min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border px-3 py-2 text-left text-xs font-medium transition ${
                  motif === courant
                    ? 'border-codialis-navy bg-codialis-navy text-white'
                    : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                {courant}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-codialis-navy">
            Motif du refus<MarqueObligatoire />
          </span>
          <textarea
            required
            rows={3}
            maxLength={300}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="Précisez ce qui doit être corrigé avant un nouveau dépôt."
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
          <span className="mt-1 block text-[11px] text-codialis-gray">
            {motif.trim().length}/300 caractères.
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <button
            type="submit"
            disabled={enCours || motif.trim() === ''}
            className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enCours ? 'Enregistrement…' : 'Refuser l’exemplaire'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
